import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { dateToYMDformat, getEndDate } from 'utils/date'
import { STATUS } from 'utils/constants'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'
import { v7 } from 'uuid'

export const BASKET_TYPES = {
  rent: 'rent',
  shop: 'shop',
  rentAddon: 'rentAddon'
}

export const DELIVERY_ACTIONS = {
  add: 'add',
  remove: 'remove',
  moveToTheBeginning: 'moveToTheBeginning',
  edit: 'edit'
}

const basketItem = {
  type: BASKET_TYPES.rent,
  object: 'Warehouse',
  start: '',
  hash: '',
  end: '',
  name: '',
  amount: 1,
  placeId: '',
  address: '',
  dailyPrice: '',
  monthlyPrice: '',
  hasOnlineAccess: false,
  periodsToPay: 0,
  termiateDays: 0,
}

const initialState = {
  cartId: '',
  invoice: false,
  unlock: false,
  backup: false,
  time: 0,
  lastSentTime: 0,
  errors: false,
  paymentInProgress: false,
  terms: {
    rentalTerms: false,
    privacyPolicy: false,
    termsAndConditions: false,
  },
  discountCode: '',
  selectedWarehouses: [],
  checkPrice: {},
  list: [],
  delivery: [],
  toPay: {}
}

const THREE_MINUTES = 1000 * 60 * 3

export const getCheckPrice = createAsyncThunk('basket/getCheckPrice', async (_, thunkAPI) => {
  const state = thunkAPI.getState()
  const userID = state.login.user?.id || ''
  const selectedWarehouses = state.basket?.selectedWarehouses || []
  const body = {
    userID,
    objects: selectedWarehouses.map(({ placeId, hash, id, cartId }) => {
      const object = {
        object: 'Warehouse',
        placeID: placeId,
        cartId
      }
      if (id) object.id = id
      else object.hash = hash
      return object
    })
  }
  const response = await POST(urls.checkPrice, { body })
  return response.objects ? response.objects : response
})

export const sendBasket = createAsyncThunk('basket/send', async (args, thunkAPI) => {
  const store = thunkAPI.getState()
  const { cartId, list, invoice, discountCode, newDocuments, delivery, time, toPay } = store.basket
  const body = {
    cartId,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    companyId: invoice?.id || '',
    time: time + THREE_MINUTES,
    delivery: Array.isArray(delivery)
      ? delivery.reduce((r, d) => {
        r[d.id] = d
        return r
      }, {})
      : {},
    list: list.map(({ identifier, id, name, notify, object, type, hash, placeId, insurance, pricelistId, start, end, address, objectId, description, deliveryId, related, ownerId, reservation, dailyPrice, monthlyPrice, equipment }) => {
      const item = (() => {
        switch (type) {
          case BASKET_TYPES.rent: {
            return {
              identifier,
              type,
              object,
              id,
              parameters: {
                hash,
                placeId,
                pricelistId,
                reservation,
                insurance,
                startDate: start,
                endDate: end,
                equipment: equipment ? Object.values(equipment) : undefined,
                monthlyPrice,
                dailyPrice,
                address
              }
            }
          }

          case BASKET_TYPES.rentAddon: {
            return {
              identifier,
              type,
              id: id || null,
              object: 'LockUserOpeningMethod',
              parameters: {
                name,
                placeId: placeId || undefined,
                object,
                objectId,
                description,
                deliveryId,
                related,
                ownerId
              }
            }
          }

          case BASKET_TYPES.shop: {
            return {
              identifier,
              type,
              id,
              object,
              parameters: {
                name,
                description,
                deliveryId,
                related
              }
            }
          }

          default: return
        }
      })()
      if (item && notify) item.parameters.notify = notify
      return item
    })
  }

  if (args?.discountCode || discountCode) {
    body.discountCode = args?.discountCode || discountCode
  }

  if (toPay) {
    const hashes = getSelectedHashes(store.basket)
    if (hashes.length > 0) {
      body.selectedHashes = hashes
    }
  }

  if (args?.step) {
    body.step = args.step

    if (args.step === 'newDocument' || args.step === 'finalize') {
      const hashes = getSelectedHashes(store.basket)
      const newDocument = {
        periods_to_pay: hashes.length === 0 ? '1m' : hashes,
        type: 'invoice',
        payments: args?.payments
      }
      if (args?.step === 'finalize' && newDocuments) {
        newDocument.invoiceIds = newDocuments.map(d => d.id)
      }

      body.newDocument = newDocument
    }
  }

  return await POST(urls.cart, { body })
})

const getSelectedHashes = (basket) => {
  const { list, toPay } = basket
  return list.reduce((r, { type, identifier, periodsToPay }) => {
    if (type === BASKET_TYPES.rent) {
      const periods_to_pay = toPay?.[identifier]?.periods_to_pay
      const hashes = periods_to_pay?.[periodsToPay]?.hashes
      if (hashes) {
        r.push(...hashes)
      }
    }
    return r
  }, [])
}

export const basketSlice = createSlice({
  name: 'basket',
  initialState,
  reducers: {
    updatePaymentInProgress: (state, action) => {
      if (!action.payload) {
        state.time = Date.now()
      }
      state.paymentInProgress = action.payload
    },
    addItemToBasket: (state, action) => {
      const { payload } = action
      if (typeof payload !== 'object') return
      if (state.list.length === 0) {
        state.cartId = v7()
      }
      const newBasketItem = { identifier: v7(), ...basketItem, ...payload }
      state.list.push(newBasketItem)
      if (newBasketItem?.type === BASKET_TYPES.rent) {
        state.terms.rentalTerms = false
        const currentIdentifiers = state.list.reduce((r, i) => {
          if (i.type === BASKET_TYPES.rent) r.push(i.identifier)
          return r
        }, [])
        state.selectedWarehouses = [
          ...state.selectedWarehouses.filter(({ cartId }) => currentIdentifiers.includes(cartId)),
          { placeId: newBasketItem.placeId, id: newBasketItem.id, hash: newBasketItem.hash, cartId: newBasketItem.identifier }
        ]
      } else if (payload?.type === BASKET_TYPES.shop) {
        state.time = Date.now()
      }
    },
    removeItemFromBasket: (state, action) => {
      const removeIdentifiers = action.payload?.removeIdentifiers
      const { list, invoice, selectedWarehouses, checkPrice } = state

      const isWarehouseRemoved = removeIdentifiers
        ? list.some(l => {
          if (l.type !== BASKET_TYPES.rent) return false
          return removeIdentifiers.includes(l.identifier)
        }) : true

      const initialList = removeIdentifiers ? list.filter(e => !removeIdentifiers.includes(e.identifier)) : undefined
      const rentList = initialList ? initialList.filter(e => e.type === BASKET_TYPES.rent) : undefined
      const newList = initialList
        ? initialList.filter(({ type, ownerId, related, placeId }) => {
          if (type === BASKET_TYPES.rentAddon) {
            return (ownerId
              ? rentList.find(e => e.identifier === ownerId)
              : related
                ? (related.every(r => r.object === 'Place' ? rentList.some(e => e.placeId === r.id) : rentList.some(e => e.identifier === r.cartId)))
                : rentList.find(e => e.placeId === placeId))
          } else {
            return true
          }
        })
        : []

      if (isWarehouseRemoved) {
        state.backup = { list, invoice }
        if (newList.length === 0) {
          state.backup = { list, invoice, selectedWarehouses, checkPrice }
          state.toPay = {}
          state.time = 0
          state.terms = {
            rentalTerms: false,
            privacyPolicy: false,
            termsAndConditions: false
          }
          state.selectedWarehouses = []
          state.checkPrice = {}
          state.lastSentTime = 0
          state.invoice = false
          state.errors = false
        }
        state.unlock = false
      }
      state.list = newList
    },
    updateTerms: (state, action) => {
      state.terms = action.payload
    },
    updateDelivery: (state, action) => {
      state.delivery = action.payload
    },
    updateDiscountCode: (state, action) => {
      state.discountCode = action.payload
      if (!action.payload) {
        delete state.discount
      }
    },
    updateBasketItem: (state, action) => {
      const { list } = state
      const { identifier, values } = action.payload

      if (identifier && typeof values === 'object') {
        state.terms.rentalTerms = false
        state.list = list.map(e => identifier === e.identifier ? { ...e, ...values } : e)
      }
    },
    updateBasketList: (state, action) => {
      const { payload } = action
      const { list } = state

      if (typeof payload === 'object') {
        state.list = list.map(e => payload[e.identifier] ? { ...e, ...payload[e.identifier] } : e)
      }
    },
    extendFormToMap: (state, action) => { state.unlock = action.payload },
    updateCompany: (state, action) => { state.invoice = action.payload },
    resetTime: (state) => { state.time = Date.now() },
    restoreBackup: (state, action) => {
      const { payload } = action
      const { backup } = state

      if (payload && backup) {
        const { list = [], invoice, selectedWarehouses, checkPrice } = backup || {}

        state.list = list
        state.time = Date.now()
        state.invoice = invoice
        if (checkPrice) state.checkPrice = checkPrice
        if (selectedWarehouses) state.selectedWarehouses = selectedWarehouses
      }
      state.backup = false
      state.lastSentTime = 0
    },
    finalizeBasket: (state, action) => {
      return {
        ...initialState,
        contracts: action.payload
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCheckPrice.fulfilled, (state, action) => {
        if (!action.payload) return
        const checkPrice = {}
        for (const key in action.payload) {
          const { pricelists, pricelistId, rental_min_days, equipment, ...rest } = action.payload[key]
          const priceList = pricelists[pricelistId]?.slider

          if (priceList) {
            const lastElement = priceList.at(-1)
            priceList[priceList.length - 1] = { ...lastElement, v: '∞', m: -1, r: 100 }
          }

          checkPrice[key] = {
            ...rest,
            equipment,
            priceList,
            pricelistId,
            status: STATUS.succeed,
            termiateDays: rental_min_days
          }
        }

        state.list = state.list.map((item) => {
          if (item.type === BASKET_TYPES.rent && !item.ok) {
            const { priceList, pricelistId, termiateDays, equipment, calendar } = checkPrice[item.identifier]
            const { m, d, gross_price: monthlyPrice, gross_price_d: dailyPrice } = priceList.at(-1)
            const rangeDate = item.rangeDate || { m, d }
            const start = item.start || calendar?.start || dateToYMDformat()
            const end = item.end || calendar?.end || getEndDate(rangeDate, start)
            return {
              ...item,
              start,
              end,
              ok: true,
              pricelistId,
              termiateDays,
              rangeDate,
              dailyPrice: item.dailyPrice || dailyPrice,
              monthlyPrice: item.monthlyPrice || monthlyPrice,
              equipment: equipment?.reduce((r, equipment) => {
                if (equipment.quantity > 0) {
                  r[equipment.id] = equipment
                }
                return r
              }, {}) || {},
            }
          } else {
            return item
          }
        })

        state.time = Date.now()
        state.checkPrice = checkPrice
      })
      .addCase(sendBasket.pending, (state) => {
        const toPay = state.toPay
        for (const key in toPay) {
          const { to_pay, to_pay_monthly, to_pay_without_addons, object_price, ...rest } = toPay[key]
          toPay[key] = rest
        }
        state.toPay = toPay
      })
      .addCase(sendBasket.fulfilled, (state, action) => {
        const { positions: positionsObj, checkout, rentingSummary, newDocuments, sold, contracts, discount, locked } = action.payload
        const { list } = state

        if (locked && !sold) {
          state.paymentInProgress = true

        } else if (sold) {
          return {
            ...initialState,
            contracts
          }

        } else {
          const positionsList = Object.entries(positionsObj)
          const newList = positionsList.reduce((r, [k, v]) => {
            if (v.result === 'ok') {
              r.toPay = {
                ...r.toPay,
                [k]: v
              }

            } else if (v.result === 'reservation_required') {
              r.reservations = {
                ...(r.reservations || {}),
                [k]: v?.reservation
              }

            } else if (v.result === 'error') {
              const item = list.find(({ identifier }) => identifier === k)
              if (item) {
                r.errors.push({ ...item, result: v.result, errors: v.errors })
              }
            }
            return r
          }, { toPay: {}, reservations: undefined, errors: [] })

          state.toPay = newList.toPay
          state.discount = discount
          state.errors = newList.errors.length > 0 ? newList.errors : false
          state.lastSentTime = state.time + THREE_MINUTES - 500
          if (newList.reservations) {
            state.list = state.list.map(item => {
              return newList.reservations[item.identifier]
                ? { ...item, reservation: newList.reservations[item.identifier] }
                : item
            })
          }
          if (newDocuments) {
            state.newDocuments = newDocuments
          }
          if (checkout) {
            state.checkout = checkout
          }
          if (rentingSummary) {
            state.rentingSummary = rentingSummary
          }
        }
      })
  }
})

export const { updatePaymentInProgress, addItemToBasket, removeItemFromBasket, updateDiscountCode, updateDelivery, changeSelectedItem, updateBasketList, updateBasketItem, extendFormToMap, updateTerms, updateCompany, resetTime, restoreBackup, finalizeBasket } = basketSlice.actions

export const selectBasket = state => state.basket
export const selectBasketTime = state => state.basket.time
export const selectBasketList = state => state.basket.list
export const selectBasketToPay = state => state.basket.toPay
export const selectBasketTerms = state => state.basket.terms
export const selectBasketCheckout = state => state.basket.checkout
export const selectBasketDiscount = state => state.basket.discount
export const selectBasketContracts = state => state.basket.contracts
export const selectBasketDeliveries = state => state.basket.delivery
export const selectBasketCheckPrice = state => state.basket.checkPrice
export const selectBasketDiscountCode = state => state.basket.discountCode
export const selectBasketIsReservationUnlocked = state => state.basket.unlock
export const selectBasketRentingSummary = state => state.basket.rentingSummary
export const selectBasketPaymentInProgress = state => state.basket.paymentInProgress
export const selectBasketSelectedWarehouses = state => state.basket.selectedWarehouses
export const selectBasketIsEmpty = state => Array.isArray(state.basket.list) ? state.basket.list.length === 0 : true
export const selectBasketRedirect = (state, identifier) => {
  if (typeof identifier === 'string' && state.basket.list.some(item => item.identifier === identifier)) return
  return state.basket.list.find(item => item.type === BASKET_TYPES.rent)?.identifier || -1
}
export const selectBasketCheckPriceItem = (state, identifier) => {
  return state.basket.checkPrice?.[identifier] || {}
}
export const selectBasketItem = (state, id) => {
  const { list } = state.basket
  if (typeof id === 'number') {
    return list[id] || {}
  }

  if (typeof id === 'string') {
    const index = list.findIndex(item => item.identifier === id)
    return { ...list[index], index } || {}
  }

  const index = list.findLastIndex(item => item.type === BASKET_TYPES.rent)
  return { ...list[index], index } || {}
}

export default basketSlice.reducer
